import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Iconify from "components/iconify";
import { useTranslation } from "i18n";

import {
  allAppCurrencies,
  appLanguages,
  defaultCurrency,
  defaultLanguage,
} from "utils/Currency&Language";

import "./currency-languages-modal.css";

export default function CurrencyModal({
  open,
  onCloseModal,
  currentCurrency,
  onHandleChangeCurrentCurrency,
}) {
  const { language, setLanguage, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [lang, setLang] = useState(language?.locale || defaultLanguage.locale);
  const [currency, setCurrency] = useState(
    currentCurrency?.currency || defaultCurrency.currency
  );

  useEffect(() => {
    setCurrency(currentCurrency?.currency || defaultCurrency.currency);
  }, [currentCurrency]);

  useEffect(() => {
    setLang(language?.locale || defaultLanguage.locale);
  }, [language]);

  const handleUpdateCurrency = (selectedCurrency) => {
    onHandleChangeCurrentCurrency?.({ selectedCurrency });

    const section = window.location.pathname.split("/")[1];
    if (
      (section === "booking" || section === "confirmation") &&
      location.state?.prevPath
    ) {
      navigate(location.state.prevPath);
    }
  };

  const handleSubmit = () => {
    const getCurrency =
      allAppCurrencies.find((option) => option.currency === currency) ||
      defaultCurrency;
    const getLanguage =
      appLanguages.find((option) => option.locale === lang) || defaultLanguage;

    setLanguage(getLanguage);
    handleUpdateCurrency(getCurrency);
    onCloseModal();
  };

  const selectMenuProps = {
    disablePortal: false,
    PaperProps: {
      sx: {
        zIndex: 2000,
        mt: 0.5,
        borderRadius: "12px",
        boxShadow: "0 16px 36px rgba(8, 21, 43, 0.16)",
      },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onCloseModal}
      className="lang-currency-dialog"
      aria-labelledby="lang-currency-title"
      sx={{
        ".MuiBackdrop-root": {
          backgroundColor: "rgba(8, 21, 43, 0.52)",
        },
        ".MuiPaper-root": {
          width: { xs: "92%", sm: "440px" },
          maxWidth: "440px",
          borderRadius: "20px",
          overflow: "visible",
          boxShadow: "0 28px 64px rgba(8, 21, 43, 0.24)",
        },
      }}
    >
      <Box className="lang-currency-modal">
        <IconButton
          onClick={onCloseModal}
          size="small"
          className="lang-currency-modal__close"
          aria-label={t("common.close")}
        >
          <CloseIcon sx={{ width: "18px" }} />
        </IconButton>

        <Typography id="lang-currency-title" className="lang-currency-modal__title">
          {t("languageModal.title")}
        </Typography>
        <Typography className="lang-currency-modal__copy">
          {t("languageModal.description")}
        </Typography>

        <label className="lang-currency-modal__label">
          {t("languageModal.language")}
        </label>
        <TextField
          select
          fullWidth
          onChange={(e) => setLang(e.target.value)}
          value={lang}
          variant="outlined"
          size="small"
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          {appLanguages.map((option) => (
            <MenuItem key={option.locale} value={option.locale}>
              <Iconify
                icon={`circle-flags:${option.code?.toLowerCase()}`}
                sx={{ mr: 1 }}
              />
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </TextField>

        <label className="lang-currency-modal__label">
          {t("languageModal.currency")}
        </label>
        <TextField
          select
          fullWidth
          onChange={(e) => setCurrency(e.target.value)}
          value={currency}
          variant="outlined"
          size="small"
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          {allAppCurrencies.map((option) => (
            <MenuItem key={option.currency} value={option.currency}>
              {option.currency}
            </MenuItem>
          ))}
        </TextField>

        <Button
          variant="contained"
          onClick={handleSubmit}
          fullWidth
          className="lang-currency-modal__save"
        >
          {t("languageModal.save")}
        </Button>
      </Box>
    </Dialog>
  );
}
